import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { tableStyles } from '../ui';
import {
  getChannelMapping,
  refreshChannelMapping,
  seedChannelMapping,
  addChannelMappingEntry,
  updateChannelMappingEntry,
  deleteChannelMappingEntry,
  resetChannelMappingEntry,
  renameChannelMappingEntry,
  scanChannelFolders,
} from '../../services/adminApi';

interface ChannelMappingEntry {
  channelName: string;
  youtubeChannelId: string;
  source: 'channelmeter' | 'airtable' | 'manual' | 'legacy_seed';
  channelUrl?: string;
  lastSeenInCM?: string;
  manuallyEdited?: boolean;
  driveFolderId?: string;
  addedAt: string;
  updatedAt: string;
}

interface UnmappedChannelId {
  channelId: string;
  ownerName: string;
}

interface MappingDoc {
  entries: Record<string, ChannelMappingEntry>;
  unmappedChannelIds?: UnmappedChannelId[];
  stats: {
    total: number;
    channelmeter: number;
    airtable: number;
    manual: number;
    legacySeed: number;
  };
  lastRefreshedAt: string | null;
  message?: string;
}

const ChannelMappingTab: React.FC = () => {
  const [doc, setDoc] = useState<MappingDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Inline edit state (YouTube Channel ID)
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [savingKey, setSavingKey] = useState<string | null>(null);

  // Inline rename state (Channel Name)
  const [renamingKey, setRenamingKey] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  // Add channel form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [newChannelName, setNewChannelName] = useState('');
  const [newChannelId, setNewChannelId] = useState('');
  const [adding, setAdding] = useState(false);

  // Delete confirmation
  const [deletingKey, setDeletingKey] = useState<string | null>(null);

  // Folder scan state
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState<{ matched: number; unmatched: string[] } | null>(null);

  // Inline folder ID edit state
  const [editingFolderKey, setEditingFolderKey] = useState<string | null>(null);
  const [editFolderValue, setEditFolderValue] = useState('');

  const loadMapping = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getChannelMapping();
      setDoc(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load channel mapping.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMapping();
  }, [loadMapping]);

  // Build sorted entries list
  const entries = useMemo(() => {
    if (!doc?.entries) return [];
    return Object.entries(doc.entries)
      .map(([key, entry]) => ({ key, ...entry }))
      .sort((a, b) => a.channelName.localeCompare(b.channelName));
  }, [doc]);

  const filteredEntries = useMemo(() => {
    if (!searchQuery.trim()) return entries;
    const q = searchQuery.toLowerCase();
    return entries.filter(
      (e) =>
        e.channelName.toLowerCase().includes(q) ||
        e.youtubeChannelId.toLowerCase().includes(q) ||
        e.source.toLowerCase().includes(q) ||
        e.driveFolderId?.toLowerCase().includes(q)
    );
  }, [entries, searchQuery]);

  const stats = doc?.stats || { total: 0, channelmeter: 0, airtable: 0, manual: 0, legacySeed: 0 };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    setScanResult(null);
    try {
      const data = await refreshChannelMapping();
      setDoc(data);
    } catch (err: any) {
      setError(err.message || 'Failed to refresh channel mapping.');
    } finally {
      setRefreshing(false);
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    setError(null);
    try {
      const data = await seedChannelMapping();
      setDoc(data);
    } catch (err: any) {
      setError(err.message || 'Failed to seed channel mapping.');
    } finally {
      setSeeding(false);
    }
  };

  const handleStartEdit = (key: string, currentId: string) => {
    setEditingKey(key);
    setEditValue(currentId);
  };

  const handleCancelEdit = () => {
    setEditingKey(null);
    setEditValue('');
  };

  const handleSaveEdit = async (key: string) => {
    if (!editValue.trim()) return;
    setSavingKey(key);
    try {
      await updateChannelMappingEntry(key, { youtubeChannelId: editValue.trim() });
      await loadMapping();
      setEditingKey(null);
      setEditValue('');
    } catch (err: any) {
      setError(err.message || 'Failed to update channel.');
    } finally {
      setSavingKey(null);
    }
  };

  const handleStartRename = (key: string, currentName: string) => {
    setRenamingKey(key);
    setRenameValue(currentName);
  };

  const handleCancelRename = () => {
    setRenamingKey(null);
    setRenameValue('');
  };

  const handleSaveRename = async (key: string) => {
    if (!renameValue.trim()) return;
    setSavingKey(key);
    try {
      await renameChannelMappingEntry(key, renameValue.trim());
      await loadMapping();
      setRenamingKey(null);
      setRenameValue('');
    } catch (err: any) {
      setError(err.message || 'Failed to rename channel.');
    } finally {
      setSavingKey(null);
    }
  };

  const handleAddChannel = async () => {
    if (!newChannelName.trim() || !newChannelId.trim()) return;
    setAdding(true);
    try {
      await addChannelMappingEntry({
        channelName: newChannelName.trim(),
        youtubeChannelId: newChannelId.trim(),
      });
      await loadMapping();
      setNewChannelName('');
      setNewChannelId('');
      setShowAddForm(false);
    } catch (err: any) {
      setError(err.message || 'Failed to add channel.');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (key: string) => {
    setDeletingKey(key);
    try {
      await deleteChannelMappingEntry(key);
      await loadMapping();
    } catch (err: any) {
      setError(err.message || 'Failed to delete channel.');
    } finally {
      setDeletingKey(null);
    }
  };

  const handleReset = async (key: string) => {
    setSavingKey(key);
    try {
      await resetChannelMappingEntry(key);
      await loadMapping();
    } catch (err: any) {
      setError(err.message || 'Failed to reset channel.');
    } finally {
      setSavingKey(null);
    }
  };

  const handleScanFolders = async () => {
    setScanning(true);
    setScanResult(null);
    setError(null);
    try {
      const data = await scanChannelFolders();
      setScanResult({ matched: data.matched, unmatched: data.unmatched || [] });
      await loadMapping();
    } catch (err: any) {
      setError(err.message || 'Failed to scan folders.');
    } finally {
      setScanning(false);
    }
  };

  const handleStartFolderEdit = (key: string, currentFolderId: string) => {
    setEditingFolderKey(key);
    setEditFolderValue(currentFolderId || '');
  };

  const handleSaveFolderEdit = async (key: string) => {
    setSavingKey(key);
    try {
      await updateChannelMappingEntry(key, {
        driveFolderId: editFolderValue.trim() || null,
      });
      await loadMapping();
      setEditingFolderKey(null);
      setEditFolderValue('');
    } catch (err: any) {
      setError(err.message || 'Failed to update folder ID.');
    } finally {
      setSavingKey(null);
    }
  };

  const getSourceBadgeStyle = (source: string, manuallyEdited?: boolean): React.CSSProperties => {
    const base: React.CSSProperties = {
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: 600,
      textTransform: 'capitalize',
    };
    const effectiveSource = manuallyEdited ? 'manual' : source;
    switch (effectiveSource) {
      case 'channelmeter':
        return { ...base, background: 'var(--accent-soft)', color: 'var(--accent)' };
      case 'airtable':
        return { ...base, background: 'var(--success-soft)', color: 'var(--success)' };
      case 'manual':
        return { ...base, background: 'var(--partner-soft)', color: 'var(--partner)' };
      case 'legacy_seed':
        return { ...base, background: 'var(--warning-soft)', color: 'var(--warning)' };
      default:
        return { ...base, background: 'var(--cg-neutral-50)', color: 'var(--fg-2)' };
    }
  };

  const getSourceLabel = (source: string, manuallyEdited?: boolean): string => {
    if (manuallyEdited) return 'manual';
    switch (source) {
      case 'channelmeter': return 'channelmeter';
      case 'airtable': return 'airtable';
      case 'manual': return 'manual';
      case 'legacy_seed': return 'legacy';
      default: return source;
    }
  };

  if (loading) {
    return <p style={styles.loadingText}>Loading channel mapping...</p>;
  }

  const isEmpty = stats.total === 0;

  return (
    <div>
      {error && <p style={styles.errorText}>{error}</p>}

      {/* Summary Stats */}
      <div style={styles.statsRow}>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{stats.total}</div>
          <div style={styles.statLabel}>Total Channels</div>
        </div>
        <div style={{ ...styles.statCard, borderLeftColor: 'var(--accent)' }}>
          <div style={{ ...styles.statValue, color: 'var(--accent)' }}>{stats.channelmeter}</div>
          <div style={styles.statLabel}>ChannelMeter</div>
        </div>
        <div style={{ ...styles.statCard, borderLeftColor: 'var(--success)' }}>
          <div style={{ ...styles.statValue, color: 'var(--success)' }}>{stats.airtable}</div>
          <div style={styles.statLabel}>Airtable</div>
        </div>
        <div style={{ ...styles.statCard, borderLeftColor: 'var(--viz-3)' }}>
          <div style={{ ...styles.statValue, color: 'var(--viz-3)' }}>{stats.manual + stats.legacySeed}</div>
          <div style={styles.statLabel}>Manual / Legacy</div>
        </div>
        <div style={{ ...styles.statCard, borderLeftColor: 'var(--success)' }}>
          <div style={{ ...styles.statValue, color: 'var(--success)' }}>
            {Object.values(doc?.entries || {}).filter((e: ChannelMappingEntry) => e.driveFolderId).length}
          </div>
          <div style={styles.statLabel}>Folder IDs Set</div>
        </div>
      </div>

      {/* Channel Mapping Table */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <div>
            <h2 style={styles.sectionTitle}>Channel Mapping</h2>
            {doc?.lastRefreshedAt && (
              <p style={styles.lastRefreshed}>
                Last refreshed: {new Date(doc.lastRefreshedAt).toLocaleString()}
              </p>
            )}
          </div>
          <div style={styles.searchWrapper}>
            <input
              type="text"
              placeholder="Search channels..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={styles.searchInput}
            />
            {isEmpty ? (
              <button onClick={handleSeed} disabled={seeding || refreshing} style={styles.seedButton}>
                {seeding ? 'Seeding...' : 'Seed Legacy Data'}
              </button>
            ) : null}
            <button
              onClick={handleRefresh}
              disabled={refreshing || seeding || scanning}
              style={styles.refreshButton}
            >
              {refreshing ? 'Refreshing...' : 'Refresh from ChannelMeter'}
            </button>
            <button
              onClick={handleScanFolders}
              disabled={scanning || refreshing}
              style={styles.scanButton}
            >
              {scanning ? 'Scanning...' : 'Scan Drive Folders'}
            </button>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              style={styles.addButton}
            >
              {showAddForm ? 'Cancel' : '+ Add Channel'}
            </button>
          </div>
        </div>

        <p style={styles.refreshHint}>
          <strong>Refresh from ChannelMeter</strong> treats ChannelMeter as the source of truth:
          auto-detected channels no longer in ChannelMeter are removed. Manually added, Airtable,
          and edited channels (and their Drive folder IDs) are always kept. To keep an auto-detected
          channel that's leaving ChannelMeter, click <em>Edit</em> on its row — that marks it manual
          and exempts it from pruning.
        </p>

        {/* Add Channel Form */}
        {showAddForm && (
          <div style={styles.addForm}>
            <input
              type="text"
              placeholder="Channel Name"
              value={newChannelName}
              onChange={(e) => setNewChannelName(e.target.value)}
              style={styles.addInput}
            />
            <input
              type="text"
              placeholder="YouTube Channel ID (UC...)"
              value={newChannelId}
              onChange={(e) => setNewChannelId(e.target.value)}
              style={styles.addInput}
            />
            <button
              onClick={handleAddChannel}
              disabled={adding || !newChannelName.trim() || !newChannelId.trim()}
              style={styles.addSubmitButton}
            >
              {adding ? 'Adding...' : 'Add'}
            </button>
          </div>
        )}

        {scanResult && (
          <div style={{ padding: '8px 12px', background: 'var(--success-soft)', borderRadius: '8px', marginBottom: '12px', fontSize: '13px', border: '1px solid var(--cg-forest-200)' }}>
            Folder scan complete: <strong>{scanResult.matched}</strong> matched.
            {scanResult.unmatched.length > 0 && (
              <span> <strong>{scanResult.unmatched.length}</strong> unmatched: {scanResult.unmatched.slice(0, 5).join(', ')}{scanResult.unmatched.length > 5 ? '...' : ''}</span>
            )}
          </div>
        )}

        {filteredEntries.length === 0 ? (
          <p style={styles.emptyText}>
            {searchQuery
              ? 'No channels match your search.'
              : isEmpty
                ? 'No channel mapping data. Click "Seed Legacy Data" or "Refresh from ChannelMeter" to populate.'
                : 'No channel mapping data available.'}
          </p>
        ) : (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Channel Name</th>
                  <th style={styles.th}>YouTube Channel ID</th>
                  <th style={styles.th}>Source</th>
                  <th style={{ ...styles.th, width: '180px' }}>Drive Folder</th>
                  <th style={{ ...styles.th, width: '140px', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredEntries.map((entry) => (
                  <tr key={entry.key} style={styles.tr}>
                    <td style={{ ...styles.td, fontWeight: 500 }}>
                      {renamingKey === entry.key ? (
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          <input
                            type="text"
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            style={styles.editInput}
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveRename(entry.key);
                              if (e.key === 'Escape') handleCancelRename();
                            }}
                          />
                          <button
                            onClick={() => handleSaveRename(entry.key)}
                            disabled={savingKey === entry.key}
                            style={styles.saveBtn}
                          >
                            {savingKey === entry.key ? '...' : 'Save'}
                          </button>
                          <button onClick={handleCancelRename} style={styles.cancelBtn}>
                            Cancel
                          </button>
                        </div>
                      ) : (
                        entry.channelName
                      )}
                    </td>
                    <td style={{
                      ...styles.td,
                      fontFamily: 'var(--font-mono)',
                      fontSize: '12px',
                    }}>
                      {editingKey === entry.key ? (
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          <input
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            style={styles.editInput}
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveEdit(entry.key);
                              if (e.key === 'Escape') handleCancelEdit();
                            }}
                          />
                          <button
                            onClick={() => handleSaveEdit(entry.key)}
                            disabled={savingKey === entry.key}
                            style={styles.saveBtn}
                          >
                            {savingKey === entry.key ? '...' : 'Save'}
                          </button>
                          <button onClick={handleCancelEdit} style={styles.cancelBtn}>
                            Cancel
                          </button>
                        </div>
                      ) : (
                        entry.youtubeChannelId
                      )}
                    </td>
                    <td style={styles.td}>
                      <span style={getSourceBadgeStyle(entry.source, entry.manuallyEdited)}>
                        {getSourceLabel(entry.source, entry.manuallyEdited)}
                      </span>
                    </td>
                    <td style={{
                      ...styles.td,
                      fontFamily: 'var(--font-mono)',
                      fontSize: '11px',
                      maxWidth: '180px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {editingFolderKey === entry.key ? (
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                          <input
                            type="text"
                            value={editFolderValue}
                            onChange={(e) => setEditFolderValue(e.target.value)}
                            style={{ ...styles.editInput, width: '130px', fontSize: '11px' }}
                            autoFocus
                            placeholder="Drive folder ID"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveFolderEdit(entry.key);
                              if (e.key === 'Escape') { setEditingFolderKey(null); setEditFolderValue(''); }
                            }}
                          />
                          <button
                            onClick={() => handleSaveFolderEdit(entry.key)}
                            disabled={savingKey === entry.key}
                            style={styles.saveBtn}
                          >
                            {savingKey === entry.key ? '...' : 'OK'}
                          </button>
                        </div>
                      ) : (
                        <span
                          onClick={() => handleStartFolderEdit(entry.key, entry.driveFolderId || '')}
                          style={{ cursor: 'pointer', color: entry.driveFolderId ? 'inherit' : 'var(--fg-4)' }}
                          title={entry.driveFolderId ? `Click to edit. Folder: ${entry.driveFolderId}` : 'Click to set Drive folder ID'}
                        >
                          {entry.driveFolderId
                            ? entry.driveFolderId.substring(0, 14) + '...'
                            : '(none)'}
                        </span>
                      )}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      {editingKey !== entry.key && renamingKey !== entry.key && (
                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => handleStartRename(entry.key, entry.channelName)}
                            style={{ ...styles.actionBtn, color: 'var(--accent)' }}
                            title="Rename channel"
                          >
                            Rename
                          </button>
                          <button
                            onClick={() => handleStartEdit(entry.key, entry.youtubeChannelId)}
                            style={styles.actionBtn}
                            title="Edit YouTube Channel ID"
                          >
                            Edit
                          </button>
                          {entry.manuallyEdited && (
                            <button
                              onClick={() => handleReset(entry.key)}
                              disabled={savingKey === entry.key}
                              style={{ ...styles.actionBtn, color: 'var(--accent)' }}
                              title="Reset to auto-detected value on next refresh"
                            >
                              Reset
                            </button>
                          )}
                          <button
                            onClick={() => {
                              if (window.confirm(`Delete "${entry.channelName}" from mapping?`)) {
                                handleDelete(entry.key);
                              }
                            }}
                            disabled={deletingKey === entry.key}
                            style={{ ...styles.actionBtn, color: 'var(--danger)' }}
                            title="Delete channel mapping"
                          >
                            {deletingKey === entry.key ? '...' : 'Del'}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p style={styles.countText}>
          Showing {filteredEntries.length} of {entries.length} channels
        </p>
      </div>

      {/* Unmapped Channel IDs from CM User API */}
      {doc?.unmappedChannelIds && doc.unmappedChannelIds.length > 0 && (
        <div style={styles.unmappedSection}>
          <h3 style={styles.unmappedTitle}>
            New Channels ({doc.unmappedChannelIds.length})
            <span style={styles.newBadge}>Need Name Mapping</span>
          </h3>
          <p style={styles.hintText}>
            These YouTube channel IDs were found in ChannelMeter but don't have a GAM name mapping yet.
            Enter the channel name as it appears in GAM reports to add it.
          </p>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>YouTube Channel ID</th>
                  <th style={styles.th}>Owner (CM)</th>
                  <th style={{ ...styles.th, width: '200px' }}>GAM Channel Name</th>
                  <th style={{ ...styles.th, width: '80px' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {doc.unmappedChannelIds.map((item) => (
                  <UnmappedRow
                    key={item.channelId}
                    item={item}
                    onAdd={async (channelName) => {
                      try {
                        await addChannelMappingEntry({
                          channelName,
                          youtubeChannelId: item.channelId,
                        });
                        await loadMapping();
                      } catch (err: any) {
                        setError(err.message || 'Failed to add channel.');
                      }
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

/** Inline row component for unmapped channel quick-add */
const UnmappedRow: React.FC<{
  item: UnmappedChannelId;
  onAdd: (channelName: string) => Promise<void>;
}> = ({ item, onAdd }) => {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onAdd(name.trim());
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr style={styles.tr}>
      <td style={styles.td}>
        <code style={{ fontSize: '11px' }}>{item.channelId}</code>
      </td>
      <td style={styles.td}>{item.ownerName}</td>
      <td style={styles.td}>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter GAM channel name..."
          style={styles.inlineInput}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
        />
      </td>
      <td style={styles.td}>
        <button
          onClick={handleAdd}
          disabled={saving || !name.trim()}
          style={{
            ...baseButton,
            background: name.trim() ? 'var(--success)' : 'var(--bg-surface-1)',
            color: name.trim() ? '#fff' : 'var(--fg-3)',
            border: '1px solid ' + (name.trim() ? 'var(--success)' : 'var(--border-1)'),
            opacity: saving ? 0.6 : 1,
            fontSize: '12px',
            padding: '4px 10px',
          }}
        >
          {saving ? '...' : 'Add'}
        </button>
      </td>
    </tr>
  );
};

const baseButton: React.CSSProperties = {
  padding: '8px 14px',
  borderRadius: '8px',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'var(--font-sans)',
  whiteSpace: 'nowrap' as const,
};

const styles: Record<string, React.CSSProperties> = {
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  statCard: {
    background: 'var(--bg-surface-1)',
    borderRadius: '12px',
    border: '1px solid var(--border-1)',
    borderLeft: '4px solid var(--accent)',
    padding: '16px 20px',
    textAlign: 'center' as const,
  },
  statValue: {
    fontSize: '28px',
    fontWeight: 700,
    color: 'var(--fg-1)',
    lineHeight: 1.2,
  },
  statLabel: {
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--fg-2)',
    marginTop: '4px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
  },
  section: {
    background: 'var(--bg-surface-1)',
    borderRadius: '12px',
    border: '1px solid var(--border-1)',
    padding: '24px',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px',
    flexWrap: 'wrap' as const,
    gap: '12px',
  },
  sectionTitle: {
    margin: 0,
    fontSize: '16px',
    fontWeight: 600,
    color: 'var(--fg-1)',
  },
  lastRefreshed: {
    margin: '4px 0 0 0',
    fontSize: '12px',
    color: 'var(--fg-3)',
  },
  refreshHint: {
    margin: '0 0 16px',
    padding: '10px 14px',
    fontSize: '12px',
    lineHeight: '1.5',
    color: 'var(--fg-2)',
    background: 'var(--bg-app)',
    border: '1px solid var(--border-1)',
    borderRadius: '8px',
  },
  searchWrapper: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
  },
  searchInput: {
    padding: '8px 12px',
    border: '1px solid var(--border-1)',
    borderRadius: '8px',
    fontSize: '14px',
    fontFamily: 'var(--font-sans)',
    color: 'var(--fg-1)',
    background: 'var(--bg-app)',
    outline: 'none',
    minWidth: '200px',
  },
  refreshButton: {
    ...baseButton,
    background: 'var(--accent)',
    color: '#fff',
    border: 'none',
  },
  seedButton: {
    ...baseButton,
    background: 'var(--warning)',
    color: '#fff',
    border: 'none',
  },
  addButton: {
    ...baseButton,
    background: 'var(--bg-app)',
    color: 'var(--fg-2)',
    border: '1px solid var(--border-1)',
  },
  scanButton: {
    ...baseButton,
    background: 'var(--success-soft)',
    color: 'var(--success)',
    border: '1px solid var(--cg-forest-200)',
  },
  addForm: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    marginBottom: '16px',
    padding: '12px',
    background: 'var(--bg-app)',
    borderRadius: '8px',
    border: '1px solid var(--border-1)',
    flexWrap: 'wrap' as const,
  },
  addInput: {
    padding: '8px 12px',
    border: '1px solid var(--border-1)',
    borderRadius: '6px',
    fontSize: '13px',
    fontFamily: 'var(--font-sans)',
    color: 'var(--fg-1)',
    background: 'var(--bg-surface-1)',
    outline: 'none',
    flex: 1,
    minWidth: '180px',
  },
  addSubmitButton: {
    padding: '8px 16px',
    background: 'var(--accent)',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
  },
  tableWrapper: {
    ...tableStyles.wrapScroll,
    maxHeight: '500px',
    overflowY: 'auto' as const,
  },
  table: tableStyles.table,
  th: tableStyles.thSticky,
  tr: {
    transition: 'background 0.15s',
  },
  td: {
    ...tableStyles.td,
    verticalAlign: 'middle' as const,
  },
  editInput: {
    padding: '4px 8px',
    border: '1px solid var(--accent)',
    borderRadius: '4px',
    fontSize: '12px',
    fontFamily: 'var(--font-mono)',
    color: 'var(--fg-1)',
    background: 'var(--bg-surface-1)',
    outline: 'none',
    width: '230px',
  },
  saveBtn: {
    padding: '3px 10px',
    background: 'var(--success)',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  cancelBtn: {
    padding: '3px 10px',
    background: 'var(--bg-app)',
    color: 'var(--fg-2)',
    border: '1px solid var(--border-1)',
    borderRadius: '4px',
    fontSize: '11px',
    cursor: 'pointer',
  },
  actionBtn: {
    padding: '3px 8px',
    background: 'transparent',
    color: 'var(--fg-2)',
    border: '1px solid var(--border-1)',
    borderRadius: '4px',
    fontSize: '11px',
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
  },
  loadingText: {
    color: 'var(--fg-2)',
    fontSize: '14px',
  },
  emptyText: {
    color: 'var(--fg-3)',
    fontSize: '14px',
    fontStyle: 'italic',
  },
  errorText: {
    color: 'var(--danger)',
    fontSize: '13px',
    marginBottom: '12px',
  },
  countText: {
    marginTop: '12px',
    fontSize: '12px',
    color: 'var(--fg-3)',
  },
  unmappedSection: {
    marginTop: '24px',
    background: 'var(--bg-surface-1)',
    borderRadius: '12px',
    border: '1px solid var(--border-1)',
    padding: '20px',
  },
  unmappedTitle: {
    margin: '0 0 8px',
    fontSize: '15px',
    fontWeight: 600,
    color: 'var(--fg-1)',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  newBadge: {
    fontSize: '11px',
    fontWeight: 500,
    color: 'var(--warning)',
    background: 'rgba(245, 158, 11, 0.1)',
    padding: '2px 8px',
    borderRadius: '10px',
  },
  hintText: {
    margin: '0 0 12px',
    fontSize: '12px',
    color: 'var(--fg-2)',
    lineHeight: '1.5',
  },
  inlineInput: {
    width: '100%',
    padding: '4px 8px',
    fontSize: '12px',
    fontFamily: 'var(--font-sans)',
    background: 'var(--bg-app)',
    color: 'var(--fg-1)',
    border: '1px solid var(--border-1)',
    borderRadius: '4px',
    boxSizing: 'border-box' as const,
  },
};

export default ChannelMappingTab;
